'use client';

import { PsValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: PsValues;
  onValuesChange: (field: keyof PsValues, value: number | string) => void;
  translations: ToolsTranslations['ps'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof PsValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof PsValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Price per Share */}
      <div className="space-y-3">
        <label htmlFor="price-per-share" className="block text-sm font-medium text-gray-700">
          {translations.pricePerShare}
        </label>
        <div className="relative">
          <input
            id="price-per-share"
            type="number"
            min="0"
            step="0.01"
            value={values.pricePerShare}
            onChange={(e) => handleInputChange('pricePerShare', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>
        <div className="relative">
          <input
            id="price-per-share-slider"
            type="range"
            min="1"
            max="1000"
            step="1"
            value={values.pricePerShare}
            onChange={(e) => handleSliderChange('pricePerShare', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.pricePerShare} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.pricePerShare / 1000) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>1000 {translations.currencySuffix}</span>
        </div>
      </div>

      {/* Revenue per Share */}
      <div className="space-y-3">
        <label htmlFor="revenue-per-share" className="block text-sm font-medium text-gray-700">
          {translations.revenuePerShare}
        </label>
        <div className="relative">
          <input
            id="revenue-per-share"
            type="number"
            min="0"
            step="0.01"
            value={values.revenuePerShare}
            onChange={(e) => handleInputChange('revenuePerShare', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="0"
          />
          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
            {translations.currencySuffix}
          </span>
        </div>

        <div className="relative">
          <input
            id="revenue-per-share-slider"
            type="range"
            min="1"
            max="1000"
            step="1"
            value={values.revenuePerShare}
            onChange={(e) => handleSliderChange('revenuePerShare', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.revenuePerShare} slider`}
          />
          <div className="h-2 bg-gray-200 rounded-lg">
            <div 
              className="h-2 bg-blue-500 rounded-lg transition-all duration-150 ease-out"
              style={{ width: `${Math.min((values.revenuePerShare / 1000) * 100, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1 {translations.currencySuffix}</span>
          <span>1000 {translations.currencySuffix}</span>
        </div>
      </div>
    </div>
  );
}


